import { Phone, MapPin, Clock, Mail } from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";
import { QuoteForm } from "../components/QuoteForm";
import { SITE } from "../data/site";

export default function ContactPage() {
  usePageMeta(
    "Request a Quote | MacWay Estate Services — DC, MD & VA",
    "Request a quote for estate sales, cleanouts, downsizing, packing, donation pickups, decluttering, junk removal, or hoarding assistance across DC, Maryland, and Virginia. Or call (301) 500-0128."
  );

  return (
    <main data-testid="contact-page">
      <section className="bg-forest text-ivory py-20 md:py-28" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <p className="eyebrow-label">Contact / Request a Quote</p>
          <h1 className="mt-4 text-5xl sm:text-6xl font-semibold tracking-tight" data-testid="contact-headline">
            Tell us about the property
          </h1>
          <p className="mt-6 text-lg text-ivory/80 leading-relaxed max-w-2xl">
            A few details are all we need to get started. We'll review your request and reach out using your
            preferred contact method — usually with a couple of questions and a time to walk the property.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <QuoteForm />
          </div>

          <aside className="space-y-6">
            <div className="bg-forest text-ivory p-8" data-testid="contact-phone-card">
              <Phone className="w-8 h-8 text-brass" />
              <h2 className="mt-5 text-2xl font-medium">Prefer to talk?</h2>
              <p className="mt-3 text-sm text-ivory/75 leading-relaxed">
                Sometimes it's easier to explain a situation out loud. Call us — you'll reach a real person who
                can help.
              </p>
              <a href={SITE.phoneHref} className="mt-5 inline-block text-2xl font-semibold hover:text-brass transition-colors" data-testid="contact-phone-link">
                {SITE.phone}
              </a>
            </div>

            <div className="bg-white border border-linen p-8" data-testid="contact-email-card">
              <Mail className="w-8 h-8 text-brass" />
              <h2 className="mt-5 text-2xl font-medium text-ink">Prefer to write?</h2>
              <p className="mt-3 text-sm text-ink/70 leading-relaxed">
                Send us the details and any photos — we'll reply with next steps.
              </p>
              <a href={SITE.emailHref} className="mt-4 inline-block text-base font-semibold text-forest hover:text-brass transition-colors break-all" data-testid="contact-email-link">
                {SITE.email}
              </a>
            </div>

            <div className="bg-white border border-linen p-8" data-testid="contact-area-card">
              <MapPin className="w-8 h-8 text-brass" />
              <h2 className="mt-5 text-2xl font-medium text-ink">Where we work</h2>
              <p className="mt-3 text-sm text-ink/70 leading-relaxed">
                We serve homes and families throughout <strong>Washington, DC, Maryland, and Virginia</strong>.
                On the edge of the area? Ask — we'll tell you honestly if we can help.
              </p>
            </div>

            <div className="bg-white border border-linen p-8" data-testid="contact-next-card">
              <Clock className="w-8 h-8 text-brass" />
              <h2 className="mt-5 text-2xl font-medium text-ink">What happens next</h2>
              <ol className="mt-3 space-y-3 text-sm text-ink/70 leading-relaxed list-none">
                <li className="flex gap-3"><span className="font-serif font-semibold text-brass">1.</span> We review your request and reach out your preferred way.</li>
                <li className="flex gap-3"><span className="font-serif font-semibold text-brass">2.</span> We schedule a walkthrough of the property.</li>
                <li className="flex gap-3"><span className="font-serif font-semibold text-brass">3.</span> You receive a clear, written, no-obligation quote.</li>
              </ol>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}
